import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";

import { getSessionResult, isAdult1822 } from "@/lib/result.functions";
import { useT } from "@/lib/i18n";
import { useSessionLanguage } from "@/lib/use-session-language";
import { usePublishPageLanguage } from "@/lib/page-language";
import { Report10Page } from "@/components/report/Report10Page";
import { Report11Page } from "@/components/report/Report11Page";
import { Report12Page } from "@/components/report/Report12Page";
import { Report1011Page } from "@/components/report/Report1011Page";
import Report13Page from "@/components/report/Report13Page";

// `?lang=ru|az|en` — dev/QA override so a single real session can be
// rendered in any locale for print / PDF verification. Reader:
// PrintPage below prefers the query value over `data.language_key`.
// Invalid or absent → fall back to the session's own locale.
// Kept `/print`-only on purpose: the public /result page uses the
// stored locale unconditionally so a shared link can't rewrite copy.
// pdf=1 — страницу открывает рендер PDF (render-report-pdf.server.ts): ссылки
// и QR блоков рекомендаций получают utm_medium=pdf, а не web/print.
type PrintSearch = { lang?: "ru" | "az" | "en"; pdf?: boolean };
export const Route = createFileRoute("/session/$token/print")({
  head: () => ({
    meta: [{ title: "Yolutap — Report" }, { name: "robots", content: "noindex" }],
  }),
  validateSearch: (search: Record<string, unknown>): PrintSearch => {
    const raw = search.lang;
    return {
      lang: raw === "ru" || raw === "az" || raw === "en" ? raw : undefined,
      pdf: search.pdf === "1" || search.pdf === 1 || search.pdf === true ? true : undefined,
    };
  },
  component: PrintPage,
});

function PrintPage() {
  const { token } = Route.useParams();
  const { lang: overrideLang, pdf } = Route.useSearch();
  const offerMedium = pdf ? "pdf" : "web";
  const fetchResult = useServerFn(getSessionResult);

  const { data, isLoading, error } = useQuery({
    queryKey: ["session-result", token],
    queryFn: () => fetchResult({ data: { access_token: token } }),
  });

  // Locale that actually reaches the UI: override wins, otherwise the
  // session's stored locale. Read once so useT + effectiveData agree.
  // До результата — язык из лёгкого запроса сессии (A4, 13.10).
  const sessionLang = useSessionLanguage(token);
  const effectiveLang = overrideLang ?? data?.language_key ?? sessionLang;
  const t = useT(effectiveLang);
  // <html lang> = язык отчёта и после ?lang-подмены: от него зависит
  // text-transform: uppercase — «İ» в AZ только при lang="az".
  usePublishPageLanguage(effectiveLang ?? null);

  useEffect(() => {
    if (!data) return;
    const id = window.setTimeout(() => window.print(), 600);
    return () => window.clearTimeout(id);
  }, [data]);

  if (isLoading) return <p style={{ padding: 24 }}>{t("result.computing")}</p>;
  if (error)
    return (
      <p style={{ padding: 24, color: "#b00" }}>
        {error instanceof Error ? error.message : t("common.error")}
      </p>
    );
  if (!data) return <p style={{ padding: 24 }}>{t("common.no_data")}</p>;

  // Effective DTO: same shape as fetched, but with language_key swapped
  // to the override when the query param is set. Report10/11/12/1011
  // read language_key straight off the DTO, so a single field swap
  // propagates through the whole component tree.
  const dto = overrideLang ? { ...data, language_key: overrideLang } : data;

  // Renderer dispatch — must mirror session.$token.result.tsx:66-74 so the
  // printed PDF matches what the user sees on screen. The default branch
  // catches "12_13" (the most common live age group); the explicit "10_11"
  // branch is required because Report1011Page is a different 10-page layout
  // wired off a separate canonical library (report_text_library_10_11.json),
  // not a stripped-down Report10Page.
  // Path B (18-22): Report13Page takes raw props (name/age/result/lang),
  // unlike the archetype Report10/11/12 family which take a SessionResultDTO.
  if (isAdult1822(dto)) {
    return (
      <Report13Page
        name={dto.child_name}
        age={dto.age}
        result={dto.result1822}
        lang={overrideLang ?? (data.language_key === "az" ? "az" : data.language_key === "en" ? "en" : "ru")}
        offers={dto.offers}
        offerMedium={offerMedium}
      />
    );
  }
  if (dto.result.age_group === "16_18") return <Report12Page data={dto} offerMedium={offerMedium} />;
  if (dto.result.age_group === "14_15") return <Report11Page data={dto} offerMedium={offerMedium} />;
  if (dto.result.age_group === "10_11") return <Report1011Page data={dto} offerMedium={offerMedium} />;
  return <Report10Page data={dto} offerMedium={offerMedium} />;
}
